import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ConnectionTest = () => {
  const [connectionStatus, setConnectionStatus] = useState('testing');
  const [backendUrl, setBackendUrl] = useState(process.env.REACT_APP_BACKEND_URL);
  const [testResults, setTestResults] = useState([]);

  useEffect(() => {
    testConnectivity();
  }, []);

  const addTestResult = (test, status, message, details = null) => {
    setTestResults(prev => [...prev, { test, status, message, details, timestamp: new Date().toISOString() }]);
  };

  const testConnectivity = async () => {
    setConnectionStatus('testing');
    setTestResults([]);
    
    // Test 1: Basic backend health check
    try {
      addTestResult('Backend URL', 'info', `Testing: ${backendUrl}`);
      
      const response = await axios.get(`${backendUrl}/health`, { timeout: 10000 });
      addTestResult('Health Check', 'success', 'Backend health check passed', response.data);
    } catch (error) {
      addTestResult('Health Check', 'error', 'Backend health check failed', {
        error: error.message,
        code: error.code,
        status: error.response?.status
      });
    }

    // Test 2: Test endpoint
    try {
      const response = await axios.get(`${backendUrl}/test`, { timeout: 10000 });
      addTestResult('Test Endpoint', 'success', 'Test endpoint accessible', response.data);
    } catch (error) {
      addTestResult('Test Endpoint', 'error', 'Test endpoint failed', {
        error: error.message,
        code: error.code,
        status: error.response?.status
      });
    }

    // Test 3: Auth initialization
    try {
      const response = await axios.post(`${backendUrl}/auth/init-admin`, {}, { timeout: 10000 });
      addTestResult('Admin Init', 'success', 'Admin initialization checked', response.data);
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('already exists')) {
        addTestResult('Admin Init', 'success', 'Admin user already exists (expected)');
      } else {
        addTestResult('Admin Init', 'error', 'Admin initialization failed', {
          error: error.message,
          code: error.code,
          status: error.response?.status,
          data: error.response?.data
        });
      }
    }

    // Test 4: Login test
    try {
      const response = await axios.post(`${backendUrl}/auth/login`, {
        email: 'admin@ratemyservice.com',
        password: 'admin123'
      }, { timeout: 10000 });
      addTestResult('Login Test', 'success', 'Authentication working correctly', {
        user: response.data.user,
        token_received: !!response.data.access_token
      });
      setConnectionStatus('connected');
    } catch (error) {
      addTestResult('Login Test', 'error', 'Authentication failed', {
        error: error.message,
        code: error.code,
        status: error.response?.status,
        data: error.response?.data
      });
      setConnectionStatus('failed');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'text-green-600 bg-green-50';
      case 'error': return 'text-red-600 bg-red-50';
      case 'info': return 'text-blue-600 bg-blue-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'info': return 'ℹ️';
      default: return '⏳';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Connection Diagnostics</h1>
            <button
              onClick={testConnectivity}
              className="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
              disabled={connectionStatus === 'testing'}
            >
              {connectionStatus === 'testing' ? 'Testing...' : 'Retry Tests'}
            </button>
          </div>

          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-2">Backend Configuration</h2>
            <p className="text-sm text-gray-600">Backend URL: <code className="bg-gray-100 px-2 py-1 rounded">{backendUrl}</code></p>
          </div>

          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Test Results</h2>
            {testResults.length === 0 && connectionStatus === 'testing' && (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Running connectivity tests...</p>
              </div>
            )}
            
            {testResults.map((result, index) => (
              <div key={index} className={`p-4 rounded-lg border ${getStatusColor(result.status)}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center">
                    <span className="mr-2">{getStatusIcon(result.status)}</span>
                    <div>
                      <h3 className="font-medium">{result.test}</h3>
                      <p className="text-sm">{result.message}</p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-500">
                    {new Date(result.timestamp).toLocaleTimeString()}
                  </span>
                </div>
                
                {result.details && (
                  <div className="mt-3">
                    <details className="text-xs">
                      <summary className="cursor-pointer font-medium">Show Details</summary>
                      <pre className="mt-2 p-2 bg-gray-100 rounded overflow-x-auto">
                        {JSON.stringify(result.details, null, 2)}
                      </pre>
                    </details>
                  </div>
                )}
              </div>
            ))}
          </div>

          {connectionStatus === 'connected' && (
            <div className="mt-6 p-4 bg-green-50 rounded-lg">
              <h3 className="text-green-800 font-medium">✅ Connection Successful!</h3>
              <p className="text-green-700 text-sm mt-1">
                All tests passed. You can now use the authentication system with the default admin credentials.
              </p>
              <div className="mt-3 text-sm text-green-700">
                <strong>Default Credentials:</strong><br />
                Email: admin@ratemyservice.com<br />
                Password: admin123
              </div>
            </div>
          )}

          {connectionStatus === 'failed' && (
            <div className="mt-6 p-4 bg-red-50 rounded-lg">
              <h3 className="text-red-800 font-medium">❌ Connection Failed</h3>
              <p className="text-red-700 text-sm mt-1">
                Unable to connect to the backend. This might be due to:
              </p>
              <ul className="text-red-700 text-sm mt-2 list-disc list-inside">
                <li>External URL not properly configured</li>
                <li>Backend service not accessible externally</li>
                <li>Network connectivity issues</li>
                <li>CORS configuration problems</li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConnectionTest;